// BUILD-042 employee PROFILE CORRECTION REQUESTS — HR reviewer queue (Oct 19 appendix).
// Authority: docs/superpowers/decisions/2026-10-07-build-042-owner-led-onboarding-documents-and-test-roster.md (Oct 19 appendix)
//
// The DURABLE HR review surface: shows who requested a correction, the field, the current and
// proposed values and the reason, and lets an authorized reviewer resolve (correction handled
// through the normal edit screen) or reject (declined, with an explanation for the employee).
// Both decisions REQUIRE a note. Resolving NEVER writes the profile or sign-in — it only records
// the outcome. No-self-decision is enforced by the server and mirrored here to disable the
// control: a reviewer can never decide a request they submitted.
//
// Data comes from the context (GET /hr/admin/correction-requests), which the caller only reaches
// when they hold hr.correction.review; if they do not, the list is simply empty (the server
// returns 404 and the context records reviewUnavailable). No proposed/current value is shown in a
// shared badge or count — only here, to an authorized reviewer.
import { useState } from 'react';
import { useHrRequests, type CorrectionRequest } from './HrRequestsContext';
import { CollapsibleHelp } from './CollapsibleHelp';
import { formatLocalDateTime as formatRequestedAt } from './hrFormat';

const STATUS_TONE: Record<CorrectionRequest['status'], string> = {
  open: 'bg-amber-50 text-amber-700 border-amber-200',
  resolved: 'bg-green-50 text-green-700 border-green-200',
  rejected: 'bg-gray-100 text-gray-500 border-gray-200',
};
const STATUS_LABEL: Record<CorrectionRequest['status'], string> = {
  open: 'Awaiting review', resolved: 'Resolved', rejected: 'Rejected',
};

export function CorrectionRequestsPanel({
  viewerEmployeeId, filterEmployeeId, highlightRequestId,
}: {
  viewerEmployeeId: string;
  /** BUILD-042 unified employee workspace (2026-10-07): when set, shows only this Employee's
   *  requests — used by the admin employee workspace's "History & Corrections" destination so
   *  it reads as that Employee's own history, not the full company-wide reviewer queue. Omit
   *  for the full HR Administration "Correction Requests" queue. */
  filterEmployeeId?: string;
  /** Deep-link target (from the attention list): the request to visually emphasize. */
  highlightRequestId?: string;
}) {
  const { reviewRequests, reviewUnavailable, resolveRequest, rejectRequest, canDecide } = useHrRequests();
  const requests = filterEmployeeId ? reviewRequests.filter((r) => r.employeeId === filterEmployeeId) : reviewRequests;
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [decidingOutcome, setDecidingOutcome] = useState<'resolve' | 'reject'>('resolve');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  function start(id: string, outcome: 'resolve' | 'reject') {
    setDecidingId(id); setDecidingOutcome(outcome); setNote(''); setError(null);
  }
  async function confirm() {
    if (!decidingId || !note.trim()) return;
    setError(null);
    const fn = decidingOutcome === 'resolve' ? resolveRequest : rejectRequest;
    const res = await fn(decidingId, note.trim());
    if (res.ok) { setDecidingId(null); setNote(''); }
    else setError(res.status === 409 ? 'Another reviewer already decided this request.' : res.status === 403 ? 'You cannot decide a request you submitted.' : (res.message || 'Could not record the decision.'));
  }

  return (
    <div className="space-y-4">
      {reviewUnavailable ? (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 text-sm text-gray-500" data-testid="correction-review-unavailable">
          Reviewing correction requests needs two things: the correction-review permission, and existing access to see the employee (as their manager or through HR Administration). You do not have both, so there is nothing to review here.
        </div>
      ) : requests.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 text-sm text-gray-500">
          No correction requests. When an employee asks HR to correct a governed fact (title, department, manager, hire date, employment status), it appears here for review.
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm divide-y divide-gray-100">
          {requests.map((r) => {
            const decidable = canDecide(r, viewerEmployeeId);
            const isOwnRequest = r.employeeId === viewerEmployeeId || r.requestedByEmployeeId === viewerEmployeeId;
            const highlighted = highlightRequestId === r.requestId;
            return (
              <div key={r.requestId} className={`p-4 ${highlighted ? 'ring-2 ring-[#4AABDE] ring-inset rounded' : ''}`} data-testid="correction-row" data-request-id={r.requestId} data-status={r.status} data-highlighted={highlighted ? 'true' : undefined}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-sm">
                      <span className="font-medium text-gray-900">{r.employeeName ?? 'An employee'}</span>
                      <span className="text-gray-500"> requested a correction to </span>
                      <span className="font-medium text-gray-900">{r.fieldLabel}</span>
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">Requested {formatRequestedAt(r.requestedAt)}</div>
                  </div>
                  <span className={`text-xs border rounded-full px-2.5 py-1 flex-shrink-0 ${STATUS_TONE[r.status]}`} data-testid="correction-status">{STATUS_LABEL[r.status]}</span>
                </div>

                <dl className="grid grid-cols-2 gap-3 text-sm mt-3">
                  <div><dt className="text-gray-500 text-xs">Current value</dt><dd className="font-medium text-gray-900">{r.currentValue ?? '—'}</dd></div>
                  <div><dt className="text-gray-500 text-xs">Proposed value</dt><dd className="font-medium text-gray-900">{r.proposedValue}</dd></div>
                  {r.reason && <div className="col-span-2"><dt className="text-gray-500 text-xs">Reason given</dt><dd className="text-gray-700">{r.reason}</dd></div>}
                  {r.status !== 'open' && r.decisionNote && (
                    <div className="col-span-2">
                      <dt className="text-gray-500 text-xs">{r.status === 'resolved' ? 'How it was handled' : 'Explanation to the employee'}</dt>
                      <dd className="text-gray-700" data-testid="correction-decision-note">{r.decisionNote}{r.decidedByName ? ` — ${r.decidedByName}` : ''}</dd>
                    </div>
                  )}
                </dl>

                {r.status === 'open' && (
                  <div className="mt-3">
                    {isOwnRequest && (
                      <p className="text-xs text-gray-400 italic mb-2">
                        You submitted this request — you cannot decide your own request (the same no-self-decision rule the system enforces for employment changes and access grants).
                      </p>
                    )}
                    {decidingId === r.requestId ? (
                      <div className="space-y-2">
                        <textarea
                          className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
                          rows={2}
                          placeholder={decidingOutcome === 'resolve' ? 'How was this correction handled? (required)' : 'Explanation for the employee (required)'}
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          data-testid="correction-note"
                        />
                        {error && <div className="text-xs text-red-600" role="alert" data-testid="correction-decision-error">{error}</div>}
                        <div className="flex gap-2">
                          <button type="button" onClick={confirm} disabled={!note.trim()} className="text-xs bg-gray-700 hover:bg-gray-800 disabled:opacity-50 text-white px-3 py-1.5 rounded" data-testid="correction-confirm">
                            {decidingOutcome === 'resolve' ? 'Confirm resolved' : 'Confirm rejection'}
                          </button>
                          <button type="button" onClick={() => { setDecidingId(null); setError(null); }} className="text-xs text-gray-500 px-3 py-1.5">Cancel</button>
                        </div>
                        {decidingOutcome === 'resolve' && (
                          <p className="text-[11px] text-gray-400">Resolving records that HR handled the correction through the normal edit screen — it does not change the profile here.</p>
                        )}
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => start(r.requestId, 'resolve')}
                          disabled={!decidable}
                          title={isOwnRequest ? 'You cannot decide your own request' : undefined}
                          className="text-xs bg-[#6FA82C] hover:bg-[#5F9124] disabled:opacity-40 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded"
                          data-testid="correction-resolve-start"
                        >
                          Mark resolved
                        </button>
                        <button type="button" onClick={() => start(r.requestId, 'reject')} disabled={!decidable} data-testid="correction-reject-start" className="text-xs border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1.5 rounded">
                          Reject with explanation
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <CollapsibleHelp>
        <p>Correction requests cover governed facts the employee can see but not edit — title, department, manager, hire date, and employment status. You see a request only for people (and facts) you can already see.</p>
        <p>Resolving a request records that HR made the change through the normal edit screen; it never changes the profile from this screen. Rejecting requires an explanation the employee can see.</p>
        <p>The requester can never decide their own request, matching the no-self-finalization rule enforced server-side for employment changes and capability grants.</p>
      </CollapsibleHelp>
    </div>
  );
}
